import { autoScrollForElements } from "@atlaskit/pragmatic-drag-and-drop-auto-scroll/element";
import {
  attachClosestEdge,
  extractClosestEdge,
  type Edge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { pointerOutsideOfPreview } from "@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import { useMutation } from "@tanstack/react-query";
import { updateWorkoutItemAttributes } from "api-client";
import { sort } from "fast-sort";
import { useEffect, useId, useRef, useState } from "react";
import { createRoot } from "react-dom/client";

import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";

import { useWorkoutItemMutationScope } from "./useWorkoutItemMutationScope";
import { useWorkoutEditStore } from "./WorkoutEditContext";

const DRAG_TYPE = "workout-item-attribute-column";

export interface WorkoutItemAttributeColumnDndAttribute {
  exerciseAttributeId: string;
  exerciseAttribute: {
    name: string;
  };
}

export interface UseWorkoutItemAttributeColumnDndOptions<
  Attribute extends WorkoutItemAttributeColumnDndAttribute,
> {
  workoutItemId: string;
  attributes: Attribute[];
  enabled: boolean;
}

export interface WorkoutItemAttributeColumnDndHeaderState {
  ref: (element: HTMLTableCellElement | null) => void;
  canDrag: boolean;
  isDragging: boolean;
  closestEdge: Edge | null;
}

export interface UseWorkoutItemAttributeColumnDndResult<
  Attribute extends WorkoutItemAttributeColumnDndAttribute,
> {
  attributes: Attribute[];
  containerRef: (element: HTMLDivElement | null) => void;
  dropIndicatorLeft: number | null;
  getHeaderState: (
    exerciseAttributeId: string,
  ) => WorkoutItemAttributeColumnDndHeaderState;
}

interface DragOverState {
  exerciseAttributeId: string;
  closestEdge: Edge | null;
  indicatorLeft: number | null;
}

export function useWorkoutItemAttributeColumnDnd<
  Attribute extends WorkoutItemAttributeColumnDndAttribute,
>(
  opts: UseWorkoutItemAttributeColumnDndOptions<Attribute>,
): UseWorkoutItemAttributeColumnDndResult<Attribute> {
  const workoutEditStore = useWorkoutEditStore();
  const [draftAttributeIds, setDraftAttributeIds] = useState<string[] | null>(
    null,
  );
  const latestUpdateTickRef = useRef(0);
  const scope = useWorkoutItemMutationScope(opts.workoutItemId);
  const { withSaving } = useAuthenticatedNavigationContext();

  const orderedAttributes = sortAttributesByIds(
    opts.attributes,
    draftAttributeIds,
  );

  const mutation = useMutation({
    scope,
    mutationFn: async (exerciseAttributeIds: string[]) => {
      const response = await withSaving(
        updateWorkoutItemAttributes({
          path: {
            workoutId: workoutEditStore.workout.id,
            workoutItemId: opts.workoutItemId,
          },
          body: { exerciseAttributeIds },
        }),
      );
      return response.data.data;
    },
    onMutate: (exerciseAttributeIds) => {
      setDraftAttributeIds(exerciseAttributeIds);
      const updateTick = latestUpdateTickRef.current + 1;
      latestUpdateTickRef.current = updateTick;
      return updateTick;
    },
    // `updateTick` ignores responses from reorders that a newer drop superseded.
    onSettled: (data, _error, _input, updateTick) => {
      if (latestUpdateTickRef.current !== updateTick) {
        return;
      }

      if (data) {
        workoutEditStore.updateWorkoutItemAttributes(data);
      }

      setDraftAttributeIds(null);
    },
  });

  const dnd = useAttributeColumnDnd({
    workoutItemId: opts.workoutItemId,
    attributes: orderedAttributes,
    enabled: opts.enabled,
    onReorder: mutation.mutate,
  });

  return {
    attributes: orderedAttributes,
    containerRef: dnd.containerRef,
    dropIndicatorLeft: dnd.dropIndicatorLeft,
    getHeaderState: dnd.getHeaderState,
  };
}

interface UseAttributeColumnDndOptions<
  Attribute extends WorkoutItemAttributeColumnDndAttribute,
> {
  workoutItemId: string;
  attributes: Attribute[];
  enabled: boolean;
  onReorder: (exerciseAttributeIds: string[]) => void;
}

interface UseAttributeColumnDndResult {
  containerRef: (element: HTMLDivElement | null) => void;
  dropIndicatorLeft: number | null;
  getHeaderState: (
    exerciseAttributeId: string,
  ) => WorkoutItemAttributeColumnDndHeaderState;
}

function useAttributeColumnDnd<
  Attribute extends WorkoutItemAttributeColumnDndAttribute,
>(opts: UseAttributeColumnDndOptions<Attribute>): UseAttributeColumnDndResult {
  const contextId = useId();
  const [draggingAttributeId, setDraggingAttributeId] = useState<string | null>(
    null,
  );
  const [dragOverState, setDragOverState] = useState<DragOverState | null>(
    null,
  );

  const elementsRef = useRef<Record<string, HTMLTableCellElement | null>>({});
  const containerElRef = useRef<HTMLDivElement | null>(null);

  const orderedAttributeIds = opts.attributes.map((attribute) => {
    return attribute.exerciseAttributeId;
  });
  const orderedAttributeIdsKey = orderedAttributeIds.join("|");
  const canDrag = opts.enabled && orderedAttributeIds.length > 1;

  useEffect(() => {
    if (!canDrag) {
      return;
    }

    function buildData(exerciseAttributeId: string) {
      return {
        contextId,
        type: DRAG_TYPE,
        workoutItemId: opts.workoutItemId,
        exerciseAttributeId,
      };
    }

    const cleanups = opts.attributes.map((attribute) => {
      const element = elementsRef.current[attribute.exerciseAttributeId];

      if (!element) {
        return () => {};
      }

      function setDragOver(args: { self: { data: Record<string, unknown> } }) {
        const closestEdge = extractClosestEdge(args.self.data);
        const indicatorLeft = computeIndicatorLeft(
          containerElRef.current,
          element,
          closestEdge,
        );
        setDragOverState((current) => {
          if (
            current?.exerciseAttributeId === attribute.exerciseAttributeId &&
            current.closestEdge === closestEdge &&
            current.indicatorLeft === indicatorLeft
          ) {
            return current;
          }

          return {
            exerciseAttributeId: attribute.exerciseAttributeId,
            closestEdge,
            indicatorLeft,
          };
        });
      }

      return combine(
        draggable({
          element,
          getInitialData: () => {
            return buildData(attribute.exerciseAttributeId);
          },
          onGenerateDragPreview: (args) => {
            setCustomNativeDragPreview({
              nativeSetDragImage: args.nativeSetDragImage,
              getOffset: pointerOutsideOfPreview({ x: "8px", y: "4px" }),
              render: (renderArgs) => {
                const root = createRoot(renderArgs.container);
                root.render(
                  <div className="bg-slate-800 text-white text-xs px-2 py-1 rounded shadow-lg">
                    {attribute.exerciseAttribute.name}
                  </div>,
                );
                return () => {
                  return root.unmount();
                };
              },
            });
          },
          onDragStart: () => {
            setDraggingAttributeId(attribute.exerciseAttributeId);
          },
          onDrop: () => {
            setDraggingAttributeId(null);
          },
        }),
        dropTargetForElements({
          element,
          getIsSticky: () => {
            return true;
          },
          canDrop: (args) => {
            return (
              args.source.data.contextId === contextId &&
              args.source.data.type === DRAG_TYPE &&
              args.source.data.exerciseAttributeId !==
                attribute.exerciseAttributeId
            );
          },
          getData: (args) => {
            return attachClosestEdge(buildData(attribute.exerciseAttributeId), {
              input: args.input,
              element: args.element,
              allowedEdges: ["left", "right"],
            });
          },
          onDragEnter: setDragOver,
          onDrag: setDragOver,
          onDragLeave: () => {
            setDragOverState(null);
          },
          onDrop: () => {
            setDragOverState(null);
          },
        }),
      );
    });

    const scrollContainer = containerElRef.current;

    if (scrollContainer) {
      cleanups.push(
        autoScrollForElements({
          element: scrollContainer,
          canScroll: (args) => {
            return (
              args.source.data.contextId === contextId &&
              args.source.data.type === DRAG_TYPE &&
              scrollContainer.scrollWidth > scrollContainer.clientWidth
            );
          },
          getAllowedAxis: () => {
            return "horizontal";
          },
        }),
      );
    }

    cleanups.push(
      monitorForElements({
        canMonitor: (args) => {
          return (
            args.source.data.contextId === contextId &&
            args.source.data.type === DRAG_TYPE
          );
        },
        onDrop: (args) => {
          setDraggingAttributeId(null);
          setDragOverState(null);

          const target = args.location.current.dropTargets[0];
          const sourceAttributeId = args.source.data.exerciseAttributeId;
          const targetAttributeId = target?.data.exerciseAttributeId;

          if (
            typeof sourceAttributeId !== "string" ||
            typeof targetAttributeId !== "string"
          ) {
            return;
          }

          const nextAttributeIds = reorderAttributeIds({
            attributeIds: orderedAttributeIds,
            sourceAttributeId,
            targetAttributeId,
            closestEdge: extractClosestEdge(target.data),
          });

          if (areStringArraysEqual(nextAttributeIds, orderedAttributeIds)) {
            return;
          }

          opts.onReorder(nextAttributeIds);
        },
      }),
    );

    return () => {
      for (const cleanup of cleanups) {
        cleanup();
      }
    };
  }, [canDrag, contextId, opts.workoutItemId, orderedAttributeIdsKey]);

  function getHeaderState(
    exerciseAttributeId: string,
  ): WorkoutItemAttributeColumnDndHeaderState {
    return {
      ref: (element) => {
        elementsRef.current[exerciseAttributeId] = element;
      },
      canDrag,
      isDragging: draggingAttributeId === exerciseAttributeId,
      closestEdge:
        dragOverState?.exerciseAttributeId === exerciseAttributeId
          ? dragOverState.closestEdge
          : null,
    };
  }

  return {
    containerRef: (element) => {
      containerElRef.current = element;
    },
    dropIndicatorLeft: dragOverState?.indicatorLeft ?? null,
    getHeaderState,
  };
}

function computeIndicatorLeft(
  container: HTMLDivElement | null,
  cell: HTMLTableCellElement | null,
  closestEdge: Edge | null,
): number | null {
  if (!container || !cell || !closestEdge) {
    return null;
  }

  const containerRect = container.getBoundingClientRect();
  const cellRect = cell.getBoundingClientRect();
  const edgeX = closestEdge === "left" ? cellRect.left : cellRect.right;

  return edgeX - containerRect.left + container.scrollLeft;
}

function sortAttributesByIds<
  Attribute extends WorkoutItemAttributeColumnDndAttribute,
>(attributes: Attribute[], attributeIds: string[] | null): Attribute[] {
  if (!attributeIds) {
    return attributes;
  }

  return sort(attributes).asc((attribute) => {
    return attributeIds.indexOf(attribute.exerciseAttributeId);
  });
}

interface ReorderAttributeIdsInput {
  attributeIds: string[];
  sourceAttributeId: string;
  targetAttributeId: string;
  closestEdge: Edge | null;
}

function reorderAttributeIds(input: ReorderAttributeIdsInput): string[] {
  const sourceIndex = input.attributeIds.indexOf(input.sourceAttributeId);
  const targetIndex = input.attributeIds.indexOf(input.targetAttributeId);

  if (sourceIndex === -1 || targetIndex === -1) {
    return input.attributeIds;
  }

  const withoutSource = input.attributeIds.filter((attributeId) => {
    return attributeId !== input.sourceAttributeId;
  });
  const targetIndexWithoutSource = withoutSource.indexOf(
    input.targetAttributeId,
  );

  const shouldInsertAfter =
    input.closestEdge === "right" ||
    (input.closestEdge === null && sourceIndex < targetIndex);
  const insertIndex = shouldInsertAfter
    ? targetIndexWithoutSource + 1
    : targetIndexWithoutSource;

  return [
    ...withoutSource.slice(0, insertIndex),
    input.sourceAttributeId,
    ...withoutSource.slice(insertIndex),
  ];
}

function areStringArraysEqual(left: string[], right: string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((value, index) => {
    return value === right[index];
  });
}
